import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Payment as DisburseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as ReadyIcon
} from '@mui/icons-material';
import {
  previewLoanDisbursement,
  finalizeDeductions,
  disburseLoan,
  markLoanReadyForDisbursement
} from '../Users/api';

const Disbursement = ({ open, loan, onClose, onSuccess }) => {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [availableDeductions, setAvailableDeductions] = useState(null);
  const [calculatedTotals, setCalculatedTotals] = useState(null);
  const [deductionSelections, setDeductionSelections] = useState({
    include_processing_fee: true,
    include_advocate_fee: true,
    include_advance_deduction: true,
    custom_advance_amount: null,
    custom_deductions: []
  });
  const [customDeductions, setCustomDeductions] = useState({
    type: '',
    amount: ''
  });
  const [customAdvanceAmount, setCustomAdvanceAmount] = useState('');
  const [useCustomAdvance, setUseCustomAdvance] = useState(false);
  const [isReadyForDisbursement, setIsReadyForDisbursement] = useState(false);

  // Load preview data when dialog opens
  React.useEffect(() => {
    if (open && loan) {
      loadPreview();
      // Check if loan is already marked as ready
      setIsReadyForDisbursement(loan.is_ready_for_disbursement || false);
    } else {
      resetState();
    }
  }, [open, loan]);

  const resetState = () => {
    setAvailableDeductions(null);
    setCalculatedTotals(null);
    setDeductionSelections({
      include_processing_fee: true,
      include_advocate_fee: true,
      include_advance_deduction: true,
      custom_advance_amount: null,
      custom_deductions: []
    });
    setCustomDeductions({ type: '', amount: '' });
    setCustomAdvanceAmount('');
    setUseCustomAdvance(false);
    setIsReadyForDisbursement(false);
  };

  const loadPreview = async () => {
    try {
      setPreviewLoading(true);
      
      // Get initial available deductions (base calculation)
      const preview = await previewLoanDisbursement(loan.database_id);
      setAvailableDeductions(preview.deduction_breakdown);
      
      console.log('Loading preview for loan:', loan);
      console.log('Deductions finalized:', loan.deductionsFinalized);
      console.log('Is ready for disbursement:', loan.is_ready_for_disbursement);
      console.log('Existing deductions:', loan.deductions);
      
      // Check if loan already has finalized deductions (was marked ready before)
      if (loan.deductionsFinalized || loan.is_ready_for_disbursement) {
        // Load existing deductions from the loan
        const hasProcessingFee = loan.deductions?.some(d => d.type === 'Processing Fee');
        const hasAdvocateFee = loan.deductions?.some(d => d.type === 'Advocate Fee');
        const hasAdvanceDeduction = loan.deductions?.some(d => d.type === 'Advance Loan Deduction');
        
        // Get custom advance amount from deductions
        const advanceDeduction = loan.deductions?.find(d => d.type === 'Advance Loan Deduction');
        const customAdvanceAmt = advanceDeduction ? advanceDeduction.amount : null;
        
        const existingSelections = {
          include_processing_fee: hasProcessingFee,
          include_advocate_fee: hasAdvocateFee,
          include_advance_deduction: hasAdvanceDeduction,
          custom_advance_amount: (customAdvanceAmt && customAdvanceAmt !== preview.deduction_breakdown.advance_deduction) ? customAdvanceAmt : null,
          custom_deductions: loan.deductions?.filter(d => d.type === 'Custom Deduction')
            .map(d => ({ description: d.description, amount: d.amount })) || []
        };
        
        console.log('Existing selections loaded:', existingSelections);
        
        setDeductionSelections(existingSelections);
        
        // Check if custom advance was used
        if (customAdvanceAmt && customAdvanceAmt !== preview.deduction_breakdown.advance_deduction) {
          setUseCustomAdvance(true);
          setCustomAdvanceAmount(customAdvanceAmt.toString());
        }
        
        // Calculate totals with existing selections
        calculateTotals(preview.deduction_breakdown, existingSelections);
      } else {
        // Reset to default selections for new loan
        const initialSelections = {
          include_processing_fee: true,
          include_advocate_fee: true,
          include_advance_deduction: true,
          custom_advance_amount: null,
          custom_deductions: []
        };
        setDeductionSelections(initialSelections);
        setCustomAdvanceAmount('');
        setUseCustomAdvance(false);
        
        // Calculate initial totals
        calculateTotals(preview.deduction_breakdown, initialSelections);
      }
      
    } catch (error) {
      console.error('Error previewing disbursement:', error);
      alert('Failed to load disbursement preview. Please try again.');
      onClose();
    } finally {
      setPreviewLoading(false);
    }
  };

  const calculateTotals = (baseDeductions, selections) => {
    const processing_fee = selections.include_processing_fee ? baseDeductions.processing_fee : 0;
    const advocate_fee = selections.include_advocate_fee ? baseDeductions.advocate_fee : 0;
    
    let advance_deduction = 0;
    if (selections.include_advance_deduction) {
      advance_deduction = selections.custom_advance_amount !== null 
        ? parseFloat(selections.custom_advance_amount) 
        : baseDeductions.advance_deduction;
    }
    
    const custom_total = selections.custom_deductions.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    const total_deductions = processing_fee + advocate_fee + advance_deduction + custom_total;
    const net_disbursement = baseDeductions.principal_amount - total_deductions;
    
    setCalculatedTotals({
      processing_fee,
      advocate_fee,
      advance_deduction,
      custom_total,
      total_deductions,
      net_disbursement,
      principal_amount: baseDeductions.principal_amount,
      repayment_amount: baseDeductions.repayment_amount
    });
  };

  const handleToggleDeduction = (deductionType) => {
    const newSelections = {
      ...deductionSelections,
      [deductionType]: !deductionSelections[deductionType]
    };
    setDeductionSelections(newSelections);
    calculateTotals(availableDeductions, newSelections);
  };

  const handleCustomDeductionChange = (field, value) => {
    setCustomDeductions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddCustomDeduction = () => {
    if (!customDeductions.type.trim() || !customDeductions.amount || parseFloat(customDeductions.amount) <= 0) {
      alert('Please enter valid deduction type and amount');
      return;
    }

    const newCustomDeduction = {
      description: customDeductions.type.trim(),
      amount: parseFloat(customDeductions.amount)
    };

    const newSelections = {
      ...deductionSelections,
      custom_deductions: [...deductionSelections.custom_deductions, newCustomDeduction]
    };

    setDeductionSelections(newSelections);
    calculateTotals(availableDeductions, newSelections);
    setCustomDeductions({ type: '', amount: '' });
  };

  const handleRemoveCustomDeduction = (index) => {
    const newSelections = {
      ...deductionSelections,
      custom_deductions: deductionSelections.custom_deductions.filter((_, i) => i !== index)
    };
    setDeductionSelections(newSelections);
    calculateTotals(availableDeductions, newSelections);
  };

  const handleCustomAdvanceToggle = () => {
    const newUseCustom = !useCustomAdvance;
    setUseCustomAdvance(newUseCustom);
    
    if (!newUseCustom) {
      setCustomAdvanceAmount('');
      const newSelections = {
        ...deductionSelections,
        custom_advance_amount: null
      };
      setDeductionSelections(newSelections);
      calculateTotals(availableDeductions, newSelections);
    }
  };

  const handleCustomAdvanceAmountChange = (value) => {
    setCustomAdvanceAmount(value);
    const amount = parseFloat(value) || 0;
    const newSelections = {
      ...deductionSelections,
      custom_advance_amount: amount
    };
    setDeductionSelections(newSelections);
    calculateTotals(availableDeductions, newSelections);
  };

  const handleMarkReady = async () => {
    try {
      setActionLoading(true);

      const deductionData = {
        include_processing_fee: deductionSelections.include_processing_fee,
        include_advocate_fee: deductionSelections.include_advocate_fee,
        include_advance_deduction: deductionSelections.include_advance_deduction,
        custom_advance_amount: deductionSelections.custom_advance_amount,
        custom_deductions: deductionSelections.custom_deductions
      };

      await markLoanReadyForDisbursement(loan.database_id, deductionData);
      
      setIsReadyForDisbursement(true);
      alert('Loan marked as ready for disbursement successfully!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error marking loan ready:', error);
      alert('Failed to mark loan as ready for disbursement. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFinalizeAndDisburse = async () => {
    try {
      setActionLoading(true);

      const deductionData = {
        include_processing_fee: deductionSelections.include_processing_fee,
        include_advocate_fee: deductionSelections.include_advocate_fee,
        include_advance_deduction: deductionSelections.include_advance_deduction,
        custom_advance_amount: deductionSelections.custom_advance_amount,
        custom_deductions: deductionSelections.custom_deductions
      };

      await finalizeDeductions(loan.database_id, deductionData);

      const disbursementData = {
        loan_id: loan.database_id,
        net_disbursement_amount: calculatedTotals.net_disbursement,
        deduction_details: {
          processing_fee: calculatedTotals.processing_fee,
          advocate_fee: calculatedTotals.advocate_fee,
          advance_deduction: calculatedTotals.advance_deduction,
          custom_deductions: deductionSelections.custom_deductions
        }
      };

      await disburseLoan(disbursementData);
      
      alert('Loan disbursed successfully!');
      handleClose();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error disbursing loan:', error);
      alert('Failed to disburse loan. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (!loan) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Loan Disbursement</Typography>
          {isReadyForDisbursement && (
            <Chip 
              icon={<ReadyIcon />} 
              label="Ready for Disbursement" 
              color="success" 
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {loan.member?.name} - {loan.id}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {previewLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography>Loading disbursement details...</Typography>
          </Box>
        ) : availableDeductions && calculatedTotals ? (
          <Box>
            {/* Info Alert for workflow */}
            {!isReadyForDisbursement && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Step 1: Configure deductions below, then click "Mark Ready" to save your settings.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Step 2: After marking ready, you can disburse immediately or later.
                </Typography>
              </Alert>
            )}

            {isReadyForDisbursement && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  ✓ This loan is ready for disbursement. You can modify deductions and click "Mark Ready" again to update, or proceed to disburse.
                </Typography>
              </Alert>
            )}

            {/* Base Loan Info */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Principal Amount</Typography>
                  <Typography variant="h6">{formatCurrency(availableDeductions.principal_amount)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Repayment</Typography>
                  <Typography variant="h6">{formatCurrency(availableDeductions.repayment_amount)}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Deductions Section */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default', border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Deductions</Typography>
              
              {/* Processing Fee */}
              {availableDeductions.processing_fee > 0 && (
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={deductionSelections.include_processing_fee}
                          onChange={() => handleToggleDeduction('include_processing_fee')}
                          color="primary"
                        />
                      }
                      label="Processing Fee"
                    />
                  </Box>
                  <Typography variant="h6" color={deductionSelections.include_processing_fee ? 'error.main' : 'text.disabled'}>
                    {formatCurrency(availableDeductions.processing_fee)}
                  </Typography>
                </Box>
              )}

              {/* Advocate Fee */}
              {availableDeductions.advocate_fee > 0 && (
                <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={deductionSelections.include_advocate_fee}
                          onChange={() => handleToggleDeduction('include_advocate_fee')}
                          color="primary"
                        />
                      }
                      label="Advocate Fee"
                    />
                  </Box>
                  <Typography variant="h6" color={deductionSelections.include_advocate_fee ? 'error.main' : 'text.disabled'}>
                    {formatCurrency(availableDeductions.advocate_fee)}
                  </Typography>
                </Box>
              )}

              {/* Advance Loan Deduction */}
              {availableDeductions.advance_deduction > 0 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={deductionSelections.include_advance_deduction}
                            onChange={() => handleToggleDeduction('include_advance_deduction')}
                            color="primary"
                          />
                        }
                        label="Advance Loan Deduction"
                      />
                    </Box>
                    <Typography variant="h6" color={deductionSelections.include_advance_deduction ? 'error.main' : 'text.disabled'}>
                      {useCustomAdvance && customAdvanceAmount 
                        ? formatCurrency(customAdvanceAmount)
                        : formatCurrency(availableDeductions.advance_deduction)}
                    </Typography>
                  </Box>
                  
                  {/* Custom Advance Amount Option */}
                  {deductionSelections.include_advance_deduction && (
                    <Box ml={7} mt={1} mb={2} p={2} sx={{ backgroundColor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useCustomAdvance}
                            onChange={handleCustomAdvanceToggle}
                            color="secondary"
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Use Custom Amount (Default: {formatCurrency(availableDeductions.advance_deduction)})
                          </Typography>
                        }
                      />
                      
                      {useCustomAdvance && (
                        <Box mt={2}>
                          <TextField
                            fullWidth
                            label="Custom Advance Deduction Amount"
                            type="number"
                            size="small"
                            value={customAdvanceAmount}
                            onChange={(e) => handleCustomAdvanceAmountChange(e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">KSh</InputAdornment>,
                            }}
                            helperText="Enter a custom amount to override the system default"
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>

            {/* Custom Deductions */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default', border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>Custom Deductions</Typography>
              
              {/* Add Custom Deduction Form */}
              <Grid container spacing={2} alignItems="end" mb={2}>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Deduction Type"
                    value={customDeductions.type}
                    onChange={(e) => handleCustomDeductionChange('type', e.target.value)}
                    placeholder="e.g., Service Fee, Insurance"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={customDeductions.amount}
                    onChange={(e) => handleCustomDeductionChange('amount', e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">KSh</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Button
                    variant="contained"
                    onClick={handleAddCustomDeduction}
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>

              {/* Display Custom Deductions */}
              {deductionSelections.custom_deductions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Added Custom Deductions:</Typography>
                  {deductionSelections.custom_deductions.map((deduction, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" alignItems="center" py={1} px={2} mb={1} sx={{ backgroundColor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                      <Typography>{deduction.description}</Typography>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h6" color="error.main">
                          {formatCurrency(deduction.amount)}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveCustomDeduction(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {/* Summary */}
            <Paper sx={{ p: 2, backgroundColor: 'action.hover', border: 2, borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom color="primary">Disbursement Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">Principal Amount:</Typography>
                  <Typography variant="h6">{formatCurrency(calculatedTotals.principal_amount)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Repayment Amount:</Typography>
                  <Typography variant="h6">{formatCurrency(calculatedTotals.repayment_amount)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="error">Total Deductions:</Typography>
                  <Typography variant="h6" color="error.main">{formatCurrency(calculatedTotals.total_deductions)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.main">Net Disbursement:</Typography>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(calculatedTotals.net_disbursement)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Validation Alert */}
            {calculatedTotals.net_disbursement < 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Net disbursement cannot be negative. Please reduce deductions.
              </Alert>
            )}
          </Box>
        ) : (
          <Alert severity="error">Failed to load preview data</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={actionLoading}>
          Cancel
        </Button>
        {calculatedTotals && calculatedTotals.net_disbursement >= 0 && (
          <>
            {!isReadyForDisbursement && (
              <Button 
                variant="contained" 
                color="success"
                startIcon={<ReadyIcon />}
                onClick={handleMarkReady}
                disabled={actionLoading}
              >
                {actionLoading ? 'Saving...' : 'Mark Ready'}
              </Button>
            )}
            <Tooltip 
              title={!isReadyForDisbursement ? "Please mark loan as ready first" : "Disburse loan with confirmed deductions"}
              arrow
            >
              <span>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<DisburseIcon />}
                  onClick={handleFinalizeAndDisburse}
                  disabled={actionLoading || !isReadyForDisbursement}
                  sx={{
                    opacity: !isReadyForDisbursement ? 0.5 : 1,
                  }}
                >
                  {actionLoading ? 'Disbursing...' : 'Finalize & Disburse'}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default Disbursement;